/**
 * Analytics wrapper for Plausible.
 *
 * - All calls are no-ops in non-production (`NODE_ENV !== 'production'`) so
 *   dev/test runs never spam the dashboard.
 * - All calls are no-ops when the script hasn't loaded yet (or the user has
 *   a blocker) — Plausible queues via `window.plausible` only when the script
 *   is present.
 * - The script itself is injected from `RootLayout` via `next/script`.
 *
 * Event names are kept short and snake_case to match Plausible conventions.
 */

export type AnalyticsEvent =
  | 'contact_submit'
  | 'apply_submit'
  | 'support_submit'
  | 'smartlink_click_ios'
  | 'smartlink_click_android'
  | 'smartlink_click_desktop'
  | 'portfolio_view'
  | 'web_vital';

export type EventProps = Record<string, string | number | boolean>;

type PlausibleFn = (event: string, options?: { props?: EventProps; callback?: () => void }) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

/**
 * Returns true only when analytics should actually fire.
 * Exported so tests and call-sites can branch cleanly.
 */
export function isAnalyticsEnabled(): boolean {
  if (process.env.NODE_ENV !== 'production') return false;
  if (typeof window === 'undefined') return false;
  return typeof window.plausible === 'function';
}

/**
 * Fire a custom event. Safe to call from anywhere — no-ops outside of
 * production-with-script-loaded.
 */
export function trackEvent(event: AnalyticsEvent, props?: EventProps): void {
  if (!isAnalyticsEnabled()) return;
  try {
    window.plausible!(event, props ? { props } : undefined);
  } catch {
    // never let analytics throw — it must not break the page.
  }
}

/**
 * Web Vitals reporter shape — passed straight to `useReportWebVitals`.
 * We bucket `value` to the nearest integer to keep Plausible's revenue/count
 * axes tidy (Plausible prefers integer props).
 */
export interface WebVitalMetric {
  id: string;
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
  label?: string;
}

export function reportWebVital(metric: WebVitalMetric): void {
  if (!isAnalyticsEnabled()) return;
  trackEvent('web_vital', {
    metric: metric.name,
    value: Math.round(metric.value),
    rating: metric.rating ?? 'unknown',
  });
}
